import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react';

type AiAgentContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  pendingNotification: string | null;
  pushNotification: (message: string) => void;
  clearNotification: () => void;
  toastMessage: string | null;
  showToast: (message: string) => void;
  hideToast: () => void;
};

const AiAgentContext = createContext<AiAgentContextValue | null>(null);

export const AiAgentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingNotification, setPendingNotification] = useState<string | null>(
    null,
  );
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const pushNotification = useCallback(
    (message: string) => setPendingNotification(message),
    [],
  );
  const clearNotification = useCallback(() => setPendingNotification(null), []);
  const showToast = useCallback(
    (message: string) => setToastMessage(message),
    [],
  );
  const hideToast = useCallback(() => setToastMessage(null), []);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      pendingNotification,
      pushNotification,
      clearNotification,
      toastMessage,
      showToast,
      hideToast,
    }),
    [
      isOpen,
      open,
      close,
      pendingNotification,
      pushNotification,
      clearNotification,
      toastMessage,
      showToast,
      hideToast,
    ],
  );

  return (
    <AiAgentContext.Provider value={value}>{children}</AiAgentContext.Provider>
  );
};

export const useAiAgent = (): AiAgentContextValue => {
  const ctx = useContext(AiAgentContext);
  if (!ctx) throw new Error('useAiAgent must be used within AiAgentProvider');
  return ctx;
};
